// src/features/dashboard/components/EmployeeProductivity/ApplicationDetailTable.tsx

import {
  ApplicationDetailItem,
  ApplicationDetailSummary,
} from '../../types/Employee/employeeProductivity.types';

/* ============================================================================
 * PROPS
 * ========================================================================== */

interface Props {
  data: ApplicationDetailItem[];

  /**
   * API summary:
   *
   * {
   *   totalDuration: "08:16:48",
   *   productivePercentage: 72.5
   * }
   *
   * null is allowed because the hook clears the value
   * while loading / when API data is unavailable.
   */
  summary?: ApplicationDetailSummary | null;
}

/* ============================================================================
 * CLASSIFICATION
 * ========================================================================== */

const normalizeClassification = (
  classification: string,
): 'Productive' | 'Neutral' | 'Non-Prod' => {
  switch (
    classification
      ?.trim()
      .toLowerCase()
  ) {
    case 'productive':
      return 'Productive';

    case 'non-productive':
    case 'non-prod':
      return 'Non-Prod';

    case 'no-impact':
    case 'no impact':
    case 'neutral':
      return 'Neutral';

    default:
      return 'Neutral';
  }
};

/* ============================================================================
 * PROGRESS COLOR
 * ========================================================================== */

const getProgressColor = (
  classification: string,
): string => {
  switch (
    normalizeClassification(
      classification,
    )
  ) {
    case 'Productive':
      return 'bg-[#22C55E]';

    case 'Non-Prod':
      return 'bg-[#EF4444]';

    case 'Neutral':
    default:
      return 'bg-[#F59E0B]';
  }
};

/* ============================================================================
 * STATUS BADGE
 * ========================================================================== */

const StatusBadge = ({
  status,
}: {
  status: string;
}) => {
  const normalized =
    normalizeClassification(status);

  const styles: Record<
    'Productive' | 'Neutral' | 'Non-Prod',
    string
  > = {
    Productive:
      'bg-[#198754] text-white',

    Neutral:
      'bg-[#6B7280] text-white',

    'Non-Prod':
      'bg-[#EF4444] text-white',
  };

  return (
    <span
      className={`
        inline-flex
        shrink-0
        items-center
        justify-center
        whitespace-nowrap
        rounded-full
        px-2.5
        py-[3px]
        text-[9px]
        font-semibold
        leading-4
        sm:text-[10px]
        ${styles[normalized]}
      `}
    >
      {normalized}
    </span>
  );
};

/* ============================================================================
 * FORMAT ROW TIME
 *
 * Examples:
 *
 * 05:29:24 -> 5h 29m
 * 02:06:00 -> 2h 6m
 * 00:31:12 -> 31m
 * 00:00:36 -> 1m
 * ========================================================================== */

const formatTime = (
  time: string,
): string => {
  if (!time) {
    return '0m';
  }

  const [
    hours = '0',
    minutes = '0',
    seconds = '0',
  ] = time.split(':');

  const hourValue =
    Number(hours) || 0;

  const minuteValue =
    Number(minutes) || 0;

  const secondValue =
    Number(seconds) || 0;

  if (
    hourValue === 0 &&
    minuteValue === 0
  ) {
    return secondValue > 0
      ? '1m'
      : '0m';
  }

  if (hourValue === 0) {
    return `${minuteValue}m`;
  }

  if (minuteValue === 0) {
    return `${hourValue}h`;
  }

  return `${hourValue}h ${minuteValue}m`;
};

/* ============================================================================
 * FORMAT TOTAL DURATION
 *
 * IMPORTANT:
 *
 * This uses summary.totalDuration from the API.
 *
 * Example:
 *
 * "08:16:48" -> "8h 16m"
 *
 * We intentionally do NOT calculate the total by adding the table rows.
 * ========================================================================== */

const formatTotalDuration = (
  duration?: string,
): string => {
  if (!duration) {
    return '0m';
  }

  const [
    hours = '0',
    minutes = '0',
  ] = duration.split(':');

  const hourValue =
    Number(hours) || 0;

  const minuteValue =
    Number(minutes) || 0;

  if (
    hourValue === 0 &&
    minuteValue === 0
  ) {
    return '0m';
  }

  if (hourValue === 0) {
    return `${minuteValue}m`;
  }

  if (minuteValue === 0) {
    return `${hourValue}h`;
  }

  return `${hourValue}h ${minuteValue}m`;
};

/* ============================================================================
 * APPLICATION DETAIL TABLE
 * ========================================================================== */

const ApplicationDetailTable = ({
  data,
  summary,
}: Props) => {
  /* ==========================================================================
   * NORMALIZE DATA
   * ======================================================================== */

  const rows =
    Array.isArray(data)
      ? data.map(
          (
            item,
            index,
          ) => ({
            id:
              item.id ??
              index + 1,

            application:
              item.application?.trim() ||
              'Unknown',

            time:
              item.time ||
              '00:00:00',

            percentage:
              Number(
                item.percentage,
              ) || 0,

            classification:
              normalizeClassification(
                item.classification,
              ),

            category:
              item.category?.trim() ||
              'Unknown',
          }),
        )
      : [];

  /* ==========================================================================
   * API SUMMARY
   *
   * Example:
   *
   * {
   *   totalDuration: "08:16:48",
   *   productivePercentage: 72.5
   * }
   * ======================================================================== */

  const totalDuration =
    summary?.totalDuration ||
    '00:00:00';

  const productivePercentage =
    Number(
      summary?.productivePercentage,
    ) || 0;

  /* ==========================================================================
   * PAGE
   * ======================================================================== */

  return (
    <div
      className="
        box-border
        w-full
        min-w-0
        max-w-full
        overflow-hidden
        rounded-[14px]
        border
        border-[#D0D5DD]
        bg-white
        p-3
        sm:rounded-[16px]
        sm:p-4
        lg:p-5
      "
    >
      {/* ======================================================================
          HEADER
      ====================================================================== */}

      <div
        className="
          mb-3
          flex
          w-full
          min-w-0
          flex-col
          items-start
          gap-2
          sm:mb-4
          sm:flex-row
          sm:items-center
          sm:justify-between
        "
      >
        <h3
          className="
            min-w-0
            truncate
            text-[13px]
            font-semibold
            text-[#002B5B]
            sm:text-[14px]
          "
        >
          Application Detail · Today
        </h3>

        <div
          className="
            max-w-full
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[5px]
            text-[9px]
            font-medium
            text-[#2563EB]
            sm:text-[10px]
          "
        >
          <span className="block truncate">
            All apps ranked by time spent ·
            productivity classification
          </span>
        </div>
      </div>

      {/* ======================================================================
          TABLE
      ====================================================================== */}

      <div
        className="
          w-full
          min-w-0
          overflow-x-auto
          overflow-y-hidden
          rounded-[10px]
          border
          border-[#EAECF0]
        "
      >
        <table
          className="
            w-full
            min-w-[620px]
            table-fixed
            border-collapse
          "
        >
          <colgroup>
            <col className="w-[27%]" />
            <col className="w-[14%]" />
            <col className="w-[27%]" />
            <col className="w-[17%]" />
            <col className="w-[15%]" />
          </colgroup>

          {/* ==================================================================
              TABLE HEADER
          ================================================================== */}

          <thead>
            <tr
              className="
                border-b
                border-[#D0D5DD]
                bg-[#F9FAFB]
              "
            >
              <th
                className="
                  px-3
                  py-2.5
                  text-left
                  text-[10px]
                  font-semibold
                  text-[#101828]
                  sm:text-[11px]
                "
              >
                Application
              </th>

              <th
                className="
                  px-3
                  py-2.5
                  text-left
                  text-[10px]
                  font-semibold
                  text-[#101828]
                  sm:text-[11px]
                "
              >
                Time
              </th>

              <th
                className="
                  px-3
                  py-2.5
                  text-left
                  text-[10px]
                  font-semibold
                  text-[#101828]
                  sm:text-[11px]
                "
              >
                % of Active
              </th>

              <th
                className="
                  px-3
                  py-2.5
                  text-left
                  text-[10px]
                  font-semibold
                  text-[#101828]
                  sm:text-[11px]
                "
              >
                Classification
              </th>

              <th
                className="
                  px-3
                  py-2.5
                  text-left
                  text-[10px]
                  font-semibold
                  text-[#101828]
                  sm:text-[11px]
                "
              >
                Category
              </th>
            </tr>
          </thead>

          {/* ==================================================================
              TABLE BODY
          ================================================================== */}

          <tbody>
            {rows.length > 0 ? (
              rows.map(
                (
                  item,
                  index,
                ) => {
                  const percentage =
                    Math.min(
                      Math.max(
                        Number(
                          item.percentage,
                        ) || 0,
                        0,
                      ),
                      100,
                    );

                  return (
                    <tr
                      key={`${item.id}-${index}`}
                      className="
                        border-b
                        border-[#EAECF0]
                        transition-colors
                        hover:bg-[#F9FAFB]
                      "
                    >
                      {/* ======================================================
                          APPLICATION
                      ====================================================== */}

                      <td
                        className="
                          min-w-0
                          px-3
                          py-2.5
                        "
                      >
                        <span
                          className="
                            block
                            truncate
                            text-[11px]
                            font-medium
                            text-[#101828]
                            sm:text-[12px]
                          "
                          title={
                            item.application
                          }
                        >
                          {
                            item.application
                          }
                        </span>
                      </td>

                      {/* ======================================================
                          TIME
                      ====================================================== */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2.5
                          text-[11px]
                          font-medium
                          text-[#101828]
                          sm:text-[12px]
                        "
                      >
                        {formatTime(
                          item.time,
                        )}
                      </td>

                      {/* ======================================================
                          PERCENTAGE
                      ====================================================== */}

                      <td
                        className="
                          min-w-0
                          px-3
                          py-2.5
                        "
                      >
                        <div
                          className="
                            flex
                            min-w-0
                            items-center
                            gap-2
                          "
                        >
                          <div
                            className="
                              h-[6px]
                              min-w-0
                              flex-1
                              overflow-hidden
                              rounded-full
                              bg-[#EAECF0]
                            "
                          >
                            <div
                              className={`
                                h-full
                                rounded-full
                                transition-all
                                duration-300
                                ${getProgressColor(
                                  item.classification,
                                )}
                              `}
                              style={{
                                width:
                                  `${percentage}%`,
                              }}
                            />
                          </div>

                          <span
                            className="
                              shrink-0
                              whitespace-nowrap
                              text-[10px]
                              font-semibold
                              text-[#101828]
                              sm:text-[11px]
                            "
                          >
                            {Number(
                              item.percentage,
                            ).toFixed(1)}
                            %
                          </span>
                        </div>
                      </td>

                      {/* ======================================================
                          CLASSIFICATION
                      ====================================================== */}

                      <td
                        className="
                          px-3
                          py-2.5
                        "
                      >
                        <StatusBadge
                          status={
                            item.classification
                          }
                        />
                      </td>

                      {/* ======================================================
                          CATEGORY
                      ====================================================== */}

                      <td
                        className="
                          min-w-0
                          px-3
                          py-2.5
                        "
                      >
                        <span
                          className="
                            block
                            truncate
                            text-[11px]
                            text-[#344054]
                            sm:text-[12px]
                          "
                          title={
                            item.category
                          }
                        >
                          {
                            item.category
                          }
                        </span>
                      </td>
                    </tr>
                  );
                },
              )
            ) : (
              <tr>
                <td
                  colSpan={5}
                  className="
                    px-3
                    py-10
                    text-center
                    text-[11px]
                    text-[#667085]
                    sm:text-[12px]
                  "
                >
                  No application data available.
                </td>
              </tr>
            )}

            {/* ==================================================================
                TOTAL ACTIVE
            ================================================================== */}

            {rows.length > 0 && (
              <tr
                className="
                  border-t-2
                  border-[#D0D5DD]
                  bg-[#F9FAFB]
                "
              >
                <td
                  className="
                    px-3
                    py-2.5
                    text-[11px]
                    font-semibold
                    text-[#101828]
                    sm:py-3
                    sm:text-[12px]
                  "
                >
                  Total Active
                </td>

                <td
                  colSpan={4}
                  className="
                    px-3
                    py-2.5
                    text-[11px]
                    font-bold
                    text-[#101828]
                    sm:py-3
                    sm:text-[13px]
                  "
                >
                  {formatTotalDuration(
                    totalDuration,
                  )}

                  {' · '}

                  {productivePercentage.toFixed(
                    1,
                  )}

                  % productive
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ======================================================================
          MOBILE HINT
      ====================================================================== */}

      <div
        className="
          mt-2
          text-right
          text-[9px]
          text-[#98A2B3]
          sm:hidden
        "
      >
        Swipe horizontally to view all columns
      </div>
    </div>
  );
};

export default ApplicationDetailTable;